import { type FC, useCallback, Fragment } from 'react'
import { useTranslation } from 'react-i18next'
import { useSetRecoilState } from 'recoil'

import { useIsAdmin } from '@/hooks/utility/useIsAdmin'
import {
  showConvertTextModalForItemIdAtom,
  showServiceTemplateModalForItemIdAtom,
  swapServiceTemplateForItemIdAtom,
} from '@/atoms/itemControlAtoms'
import { Tooltip } from '@/components/global/Tooltip/Tooltip'
import {
  ConvertToTextIcon,
  ServiceTemplateIcon,
  SwapTemplateIcon,
} from '@/components/page/Items/ItemControlOverlay/ItemControlOverlay.styled'
import type { TextControlState } from '@/types/itemControls'

type TextControlsProps = {
  itemId: string
  textState?: TextControlState
  /** The glyph of the create-service-template control; the classic text item keeps its own. */
  serviceTemplateIcon?: 'newspaper' | 'squaresPlusIcon'
}

/**
 * Controls specific to text items (rendered in toolbar):
 * - Convert to editable text icon (for case detail templates)
 * - Service information template icon (to create service templates - designers only)
 * - Swap service template icon (to swap existing service templates)
 */
export const TextControls: FC<TextControlsProps> = ({
  itemId,
  textState,
  serviceTemplateIcon = 'newspaper',
}) => {
  const { t } = useTranslation()
  const isAdmin = useIsAdmin()
  const setShowConvertTextModal = useSetRecoilState(
    showConvertTextModalForItemIdAtom,
  )
  const setShowServiceTemplateModal = useSetRecoilState(
    showServiceTemplateModalForItemIdAtom,
  )
  const setSwapServiceTemplate = useSetRecoilState(
    swapServiceTemplateForItemIdAtom,
  )

  const isServiceTemplate = textState?.isServiceInformationTemplate

  const handleConvertToText = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation()
      setShowConvertTextModal(itemId)
    },
    [setShowConvertTextModal, itemId],
  )

  const handleServiceTemplate = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation()
      setShowServiceTemplateModal(itemId)
    },
    [setShowServiceTemplateModal, itemId],
  )

  const handleSwapTemplate = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation()
      setSwapServiceTemplate(itemId)
    },
    [setSwapServiceTemplate, itemId],
  )

  return (
    <Fragment>
      {/* Service Information Template icon - only for designers when not already a service template */}
      {isAdmin && !isServiceTemplate && (
        <Tooltip text={t('itemControls.createServiceTemplate')}>
          <ServiceTemplateIcon
            onClick={handleServiceTemplate}
            icon={serviceTemplateIcon}
            aria-label={t('itemControls.createServiceTemplate')}
            data-testid="overlay-service-template-icon"
          />
        </Tooltip>
      )}

      {/* Swap Service Template icon - visible when item is a service template */}
      {(isServiceTemplate || textState?.canSwapServiceTemplate) && (
        <Tooltip text={t('itemControls.swapServiceTemplate')}>
          <SwapTemplateIcon
            onClick={handleSwapTemplate}
            icon="arrowPath"
            aria-label={t('itemControls.swapServiceTemplate')}
            data-testid="overlay-swap-template-icon"
          />
        </Tooltip>
      )}

      {/* Convert to editable text icon - only when item has case detail attributes */}
      {textState?.hasTextCaseDetailAttribute && (
        <Tooltip text={t('itemControls.makeEditable')}>
          <ConvertToTextIcon
            onClick={handleConvertToText}
            icon="pencilSquare"
            aria-label={t('itemControls.makeEditable')}
            data-testid="overlay-convert-text-icon"
          />
        </Tooltip>
      )}
    </Fragment>
  )
}
